export function summarizeMediaStreamTrackState(track: MediaStreamTrack | null | undefined) {
  if (!track) return null;

  return {
    id: track.id,
    kind: track.kind,
    label: track.label || null,
    enabled: track.enabled,
    muted: track.muted,
    readyState: track.readyState,
    contentHint: track.contentHint || null,
    settings: track.getSettings(),
    constraints: track.getConstraints(),
    capabilities: summarizeMediaTrackCapabilities(track.getCapabilities?.()),
  };
}

export function summarizeSenderParameters(sender: RTCRtpSender | null | undefined) {
  if (!sender) return null;

  const parameters = sender.getParameters() as RTCRtpSendParameters & {
    degradationPreference?: RTCDegradationPreference;
  };

  return {
    degradationPreference: parameters.degradationPreference ?? null,
    encodings: parameters.encodings?.map((encoding) => {
      const editableEncoding = encoding as RTCRtpEncodingParameters & {
        priority?: RTCPriorityType;
        networkPriority?: RTCPriorityType;
        scalabilityMode?: string;
      };

      return {
        active: editableEncoding.active ?? null,
        rid: editableEncoding.rid ?? null,
        maxBitrate: editableEncoding.maxBitrate ?? null,
        maxFramerate: editableEncoding.maxFramerate ?? null,
        scaleResolutionDownBy: editableEncoding.scaleResolutionDownBy ?? null,
        scalabilityMode: editableEncoding.scalabilityMode ?? null,
        priority: editableEncoding.priority ?? null,
        networkPriority: editableEncoding.networkPriority ?? null,
      };
    }) ?? [],
  };
}

function summarizeMediaTrackCapabilities(capabilities: MediaTrackCapabilities | undefined) {
  if (!capabilities) return null;
  const extendedCapabilities = capabilities as MediaTrackCapabilities & {
    resizeMode?: string[];
    deviceId?: string;
  };

  return {
    width: summarizeCapabilityValue(capabilities.width),
    height: summarizeCapabilityValue(capabilities.height),
    aspectRatio: summarizeCapabilityValue(capabilities.aspectRatio),
    frameRate: summarizeCapabilityValue(capabilities.frameRate),
    resizeMode: summarizeCapabilityValue(extendedCapabilities.resizeMode),
    facingMode: summarizeCapabilityValue(capabilities.facingMode),
    deviceId: extendedCapabilities.deviceId ? '[present]' : null,
  };
}

function summarizeCapabilityValue(value: unknown) {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.slice(0, 20);
  if (typeof value !== 'object') return value;

  const summarized: Record<string, string | number | boolean | null> = {};
  for (const [key, childValue] of Object.entries(value)) {
    if (
      typeof childValue === 'string' ||
      typeof childValue === 'number' ||
      typeof childValue === 'boolean' ||
      childValue === null
    ) {
      summarized[key] = childValue;
    }
  }
  return summarized;
}
